if (!customElements.get('wca-cart-reward')) {
  class WcaCartReward extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      const productsNode = this.querySelector('[data-cart-reward-products]');
      const configNode = this.querySelector('[data-cart-reward-config]');
      this.messageNode = this.querySelector('[data-cart-reward-message]');
      this.progressNode = this.querySelector('[data-cart-reward-progress]');
      this.threshold = Number(this.dataset.threshold);
      this.freeShipping = this.dataset.freeShipping === 'true';

      try {
        this.rewardProducts = JSON.parse(productsNode?.textContent || '[]').filter(
          (product) => Number(product.variantId) > 0
        );
        this.rewardConfig = JSON.parse(configNode?.textContent || '{}');
      } catch (error) {
        console.error('WCA cart reward configuration is invalid.', error);
        return;
      }

      if (!this.messageNode || !this.progressNode || !this.threshold || !this.rewardProducts.length) return;

      this.initialized = true;
      this.syncQueued = false;
      this.safetyBlocked = false;
      this.unavailableVariantIds = new Set();
      this.cartContainer = this.closest('m-cart-drawer, m-cart');
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.cartContainer?.addEventListener('change', this.variantChangeHandler, true);
      this.cartUpdateUnsubscriber = window.MinimogEvents?.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        (cart) => {
          if (cart?.source !== 'cart-reward') this.synchronize(cart?.items ? cart : null);
        }
      );
      this.synchronize();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      this.cartContainer?.removeEventListener('change', this.variantChangeHandler, true);
      this.initialized = false;
    }

    async synchronize(providedCart = null) {
      if (this.isSyncing) {
        this.syncQueued = true;
        return;
      }

      this.syncQueued = false;
      this.isSyncing = true;
      this.setCartBusy(true);

      try {
        let cart = providedCart || (await this.fetchCart());
        let qualifyingSubtotal = this.getQualifyingSubtotal(cart);
        const isQualified = qualifyingSubtotal >= this.threshold;

        this.updateProgress(qualifyingSubtotal, isQualified);

        let cartChanged = false;
        const updates = this.getRequiredUpdates(cart, isQualified);
        if (Object.keys(updates).length) {
          cart = await this.updateCart(updates);
          cartChanged = true;
        }

        const missingProducts = isQualified ? this.getMissingProducts(cart) : [];
        if (missingProducts.length) {
          cart = await this.addRewards(missingProducts);
          cartChanged = true;
        }

        if (cartChanged) {
          cart = await this.fetchCart();
          qualifyingSubtotal = this.getQualifyingSubtotal(cart);
        }

        if (this.hasPaidReward(cart)) {
          cart = await this.removeRewardLines(cart);
          cartChanged = true;
          this.showUnavailableMessage();
          console.error('WCA cart reward was removed because its Shopify discount was not applied.');
        } else {
          this.updateProgress(qualifyingSubtotal, qualifyingSubtotal >= this.threshold);
          if (isQualified && this.hasUnavailableRewards()) {
            this.setMessage(this.getMessage('partial', 'Algunos regalos no están disponibles temporalmente.'));
          }
        }

        if (this.safetyBlocked) {
          cart = await this.ensureNoPaidRewards();
          this.safetyBlocked = false;
          cartChanged = true;
        }

        if (cartChanged) {
          this.refreshCart(cart);
        }
      } catch (error) {
        this.safetyBlocked = true;
        this.showUnavailableMessage();
        console.error('WCA cart reward could not be synchronized.', error);
      } finally {
        this.isSyncing = false;
        this.classList.remove('wca-cart-reward--loading');
        this.setCartBusy(this.safetyBlocked);

        if (!this.safetyBlocked && this.syncQueued) {
          this.syncQueued = false;
          this.synchronize();
        }
      }
    }

    getQualifyingSubtotal(cart) {
      return cart.items.reduce((subtotal, item) => {
        if (this.isRewardLine(item)) return subtotal;
        return subtotal + Number(item.final_line_price || 0);
      }, 0);
    }

    getRequiredUpdates(cart, isQualified) {
      const keptProductIds = new Set();

      return cart.items.reduce((updates, item) => {
        if (!this.isRewardLine(item)) return updates;

        const productId = Number(item.product_id);
        const shouldKeep = isQualified
          && this.isConfiguredRewardItem(item)
          && !keptProductIds.has(productId);
        if (shouldKeep) keptProductIds.add(productId);
        if (!shouldKeep || item.quantity !== 1) updates[item.key] = shouldKeep ? 1 : 0;
        return updates;
      }, {});
    }

    getMissingProducts(cart) {
      const rewardProductIds = new Set(
        cart.items
          .filter((item) => this.isRewardLine(item) && this.isConfiguredRewardItem(item))
          .map((item) => Number(item.product_id))
      );

      return this.rewardProducts.filter(
        (product) => product.available && !rewardProductIds.has(Number(product.productId))
      );
    }

    hasPaidReward(cart) {
      return cart.items.some((item) => this.isRewardLine(item) && Number(item.final_line_price) > 0);
    }

    isRewardLine(item) {
      return item.properties?._wca_reward_gift === 'true';
    }

    getConfiguredProduct(productId) {
      return this.rewardProducts.find((product) => Number(product.productId) === Number(productId));
    }

    getConfiguredVariant(product, variantId) {
      const variants = Array.isArray(product?.variants) && product.variants.length
        ? product.variants
        : [{ id: product?.variantId, available: product?.available }];
      return variants.find((variant) => Number(variant.id) === Number(variantId));
    }

    isConfiguredRewardItem(item) {
      const product = this.getConfiguredProduct(item.product_id);
      return Boolean(product && this.getConfiguredVariant(product, item.variant_id));
    }

    async handleVariantChange(event) {
      const selector = event.target.closest?.('[data-cart-reward-variant-selector]');
      if (!selector || !this.cartContainer?.contains(selector)) return;
      event.stopPropagation();

      const previousVariantId = Number(selector.dataset.cartRewardCurrentVariantId);
      const nextVariantId = Number(selector.value);
      const productId = Number(selector.dataset.cartRewardProductId);
      const product = this.getConfiguredProduct(productId);
      const nextVariant = this.getConfiguredVariant(product, nextVariantId);

      if (this.isSyncing || this.safetyBlocked || !product || !nextVariant?.available || previousVariantId === nextVariantId) {
        selector.value = String(previousVariantId);
        return;
      }

      this.isSyncing = true;
      selector.disabled = true;
      this.setCartBusy(true);
      let variantChanged = false;
      let cartIsSafe = true;

      try {
        let cart = await this.fetchCart();
        const previousLine = cart.items.find(
          (item) => item.key === selector.dataset.cartRewardLineKey
            && this.isRewardLine(item)
            && Number(item.product_id) === productId
            && Number(item.variant_id) === previousVariantId
        );
        if (!previousLine) throw new Error('The current reward line could not be found.');

        await this.updateCart({ [previousLine.key]: 0 });

        try {
          await this.addRewardVariant(nextVariantId);
        } catch (error) {
          this.unavailableVariantIds.add(nextVariantId);
          throw error;
        }

        cart = await this.fetchCart();
        const nextLine = cart.items.find(
          (item) => this.isRewardLine(item)
            && Number(item.product_id) === productId
            && Number(item.variant_id) === nextVariantId
        );

        if (!nextLine || Number(nextLine.final_line_price) > 0) {
          throw new Error('The selected reward variant is unavailable or was not discounted.');
        }

        this.unavailableVariantIds.delete(nextVariantId);
        this.refreshCart(cart);
        variantChanged = true;
      } catch (error) {
        try {
          const restoredCart = await this.restoreRewardVariant(productId, previousVariantId, nextVariantId);
          this.refreshCart(restoredCart);
        } catch (restoreError) {
          console.error('WCA cart reward could not restore the previous variant.', restoreError);
          cartIsSafe = false;
          try {
            const recoveredCart = await this.ensureNoPaidRewards();
            this.refreshCart(recoveredCart);
            cartIsSafe = true;
          } catch (recoveryError) {
            console.error('WCA cart reward could not verify a safe cart state.', recoveryError);
          }
        }
        selector.value = String(previousVariantId);
        this.showUnavailableMessage();
        console.error('WCA cart reward variant could not be changed.', error);
      } finally {
        this.isSyncing = false;
        this.safetyBlocked = !cartIsSafe;
        if (!variantChanged && cartIsSafe) selector.disabled = false;
        this.classList.remove('wca-cart-reward--loading');
        this.setCartBusy(this.safetyBlocked);

        if (!this.safetyBlocked && this.syncQueued) {
          this.syncQueued = false;
          this.synchronize();
        }
      }
    }

    updateProgress(subtotal, isQualified) {
      const progress = Math.min(100, Math.round((subtotal / this.threshold) * 100));
      const labels = this.rewardConfig.labels || {};
      const rewardLabel = this.rewardProducts.length > 1
        ? labels.gifts || 'regalos'
        : labels.gift || 'regalo';
      const benefitLabel = this.freeShipping ? `${labels.shipping || 'envío gratis'} + ${rewardLabel}` : rewardLabel;

      this.style.setProperty('--wca-cart-reward-progress', `${progress}%`);
      this.progressNode.setAttribute('aria-valuenow', String(progress));

      if (isQualified) {
        this.setMessage(this.interpolateMessage(
          this.getMessage('success', '¡Listo! Tienes [beneficios].'),
          { beneficios: benefitLabel }
        ));
        return;
      }

      const remaining = Math.max(0, this.threshold - subtotal);
      this.setMessage(this.interpolateMessage(
        this.getMessage('progress', 'Compra [restante] más para obtener [beneficios].'),
        {
          restante: formatMoney(remaining, window.MinimogSettings.money_format),
          beneficios: benefitLabel,
        }
      ));
    }

    showUnavailableMessage() {
      this.setMessage(this.getMessage('unavailable', 'El regalo no está disponible temporalmente.'));
    }

    getMessage(key, fallback) {
      return this.rewardConfig.messages?.[key] || fallback;
    }

    interpolateMessage(template, replacements) {
      return Object.entries(replacements).reduce(
        (message, [key, value]) => message.split(`[${key}]`).join(value),
        template
      );
    }

    setMessage(message) {
      this.messageNode.textContent = message;
      this.progressNode.setAttribute('aria-valuetext', this.messageNode.textContent);
    }

    hasUnavailableRewards() {
      return (
        this.unavailableVariantIds.size > 0 || this.rewardProducts.some((product) => !product.available)
      );
    }

    setCartBusy(isBusy) {
      const cartContainer = this.cartContainer || this.closest('m-cart-drawer, m-cart');
      if (!cartContainer) return;

      cartContainer.classList.toggle('wca-cart-reward-cart--busy', isBusy);
      cartContainer.setAttribute('aria-busy', String(isBusy));
      cartContainer.querySelectorAll('[name="checkout"]').forEach((button) => {
        if (isBusy && !button.disabled) {
          button.disabled = true;
          button.dataset.cartRewardDisabled = 'true';
        } else if (!isBusy && button.dataset.cartRewardDisabled === 'true') {
          button.disabled = false;
          delete button.dataset.cartRewardDisabled;
        }
      });
      cartContainer.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
        if (isBusy && !container.inert) {
          container.inert = true;
          container.dataset.cartRewardInert = 'true';
        } else if (!isBusy && container.dataset.cartRewardInert === 'true') {
          container.inert = false;
          delete container.dataset.cartRewardInert;
        }
      });
      cartContainer.querySelectorAll('[data-minimog-cart-items]').forEach((container) => {
        if (isBusy && !container.inert) {
          container.inert = true;
          container.dataset.cartRewardInert = 'true';
        } else if (!isBusy && container.dataset.cartRewardInert === 'true') {
          container.inert = false;
          delete container.dataset.cartRewardInert;
        }
      });
    }

    async fetchCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}.`);
      return response.json();
    }

    async updateCart(updates) {
      this.classList.add('wca-cart-reward--loading');
      const response = await fetch(window.MinimogSettings.routes.cart_update_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error(`Cart update failed with status ${response.status}.`);
      return response.json();
    }

    async addRewards(products) {
      this.classList.add('wca-cart-reward--loading');
      let cart = null;

      for (const product of products) {
        try {
          cart = await this.addRewardVariant(product.variantId);
          this.unavailableVariantIds.delete(Number(product.variantId));
        } catch (error) {
          this.unavailableVariantIds.add(Number(product.variantId));
          console.error('WCA cart reward variant could not be added.', error);
        }
      }

      return cart;
    }

    async addRewardVariant(variantId) {
      this.classList.add('wca-cart-reward--loading');
      const response = await fetch(window.MinimogSettings.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: { _wca_reward_gift: 'true' }
            }
          ]
        })
      });
      if (!response.ok) throw new Error(`Cart add failed with status ${response.status}.`);
      return response.json();
    }

    async restoreRewardVariant(productId, previousVariantId, attemptedVariantId) {
      let cart = await this.fetchCart();
      const updates = cart.items.reduce((result, item) => {
        if (
          this.isRewardLine(item)
          && Number(item.product_id) === Number(productId)
          && Number(item.variant_id) === Number(attemptedVariantId)
        ) {
          result[item.key] = 0;
        }
        return result;
      }, {});

      if (Object.keys(updates).length) cart = await this.updateCart(updates);

      const previousExists = cart.items.some(
        (item) => this.isRewardLine(item)
          && Number(item.product_id) === Number(productId)
          && Number(item.variant_id) === Number(previousVariantId)
      );
      if (!previousExists) await this.addRewardVariant(previousVariantId);

      cart = await this.fetchCart();
      return this.hasPaidReward(cart) ? this.removeRewardLines(cart) : cart;
    }

    async ensureNoPaidRewards() {
      let cart = await this.fetchCart();
      if (this.hasPaidReward(cart)) cart = await this.removeRewardLines(cart);

      const verifiedCart = await this.fetchCart();
      if (this.hasPaidReward(verifiedCart)) {
        throw new Error('A paid reward remains in the cart.');
      }
      return verifiedCart;
    }

    async removeRewardLines(cart) {
      const updates = cart.items.reduce((result, item) => {
        if (this.isRewardLine(item)) result[item.key] = 0;
        return result;
      }, {});
      return Object.keys(updates).length ? this.updateCart(updates) : cart;
    }

    refreshCart(cart) {
      const cartDrawer = document.querySelector('m-cart-drawer');
      if (cartDrawer) cartDrawer.onCartDrawerUpdate();
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
        ...cart,
        source: 'cart-reward'
      });
    }
  }

  customElements.define('wca-cart-reward', WcaCartReward);
}
